import { Bell, MessageSquare, Calendar, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  useNotificationsQuery,
  useUnreadCount,
  useMarkReadMutation,
  useMarkAllReadMutation,
  type Notification,
} from "@/hooks/useNotifications";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

function typeIcon(type: Notification["type"]) {
  switch (type) {
    case "seller_feedback":
    case "showing_feedback":
      return <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />;
    case "showing_scheduled":
      return <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />;
    case "report_viewed":
    default:
      return <Eye className="h-4 w-4 shrink-0 text-muted-foreground" />;
  }
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function NotificationBell() {
  const navigate = useNavigate();

  const { data: session } = useQuery({
    queryKey: ["notification-bell-session"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session;
    },
    staleTime: 1000 * 60 * 5,
  });

  const userId = session?.user?.id;
  const { data: notifications } = useNotificationsQuery(userId);
  const unreadCount = useUnreadCount(userId);
  const markRead = useMarkReadMutation(userId ?? "");
  const markAllRead = useMarkAllReadMutation(userId ?? "");

  const displayCount = unreadCount > 99 ? "99+" : unreadCount > 0 ? String(unreadCount) : null;

  const handleItemClick = (notif: Notification) => {
    if (!notif.read_at && userId) {
      markRead.mutate(notif.id);
    }
    if (notif.reference_url) {
      navigate(notif.reference_url);
    } else if (notif.reference_type === "north") {
      navigate("/dashboard");
    } else if (notif.reference_type === "cma") {
      navigate("/dashboard");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9" aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {displayCount && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-destructive text-white text-[9px] font-semibold flex items-center justify-center leading-none">
              {displayCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" style={{ maxHeight: "24rem", overflowY: "auto" }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="text-sm font-semibold">Notifications</span>
          {unreadCount > 0 && (
            <button
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => userId && markAllRead.mutate()}
            >
              Mark all read
            </button>
          )}
        </div>

        {!notifications || notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No notifications yet
          </div>
        ) : (
          <div>
            {notifications.map((notif) => (
              <div
                key={notif.id}
                className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors border-b border-border/50 last:border-b-0 ${
                  !notif.read_at ? "bg-muted/30" : ""
                }`}
                onClick={() => handleItemClick(notif)}
              >
                <div className="mt-0.5">{typeIcon(notif.type)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-snug line-clamp-2">{notif.title}</p>
                  {notif.body && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{notif.body}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">{relativeTime(notif.created_at)}</p>
                </div>
                {!notif.read_at && (
                  <div className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />
                )}
              </div>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
